import { ReactElement, ReactNode } from 'react';

import { BackrsUserBadge } from '@backrs_ui/common-ui/src/atoms/BackrsUserBadge';
import { BadgeCircle } from '@backrs_ui/common-ui/src/atoms/BadgeCircle';
import { AppIcon, ICON_SIZES } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { PanelNuggetActionParams } from '@backrs_ui/common-ui/src/models/nugget';

import { Box, SxProps, Theme, Typography, Skeleton } from '@mui/material';

import { BaseNugget } from '../BaseNugget';
import { HoverButton } from '../HoverButton';
import { TypeChip } from '../TypeChip';

const INFO_CONFIG: {
  key: Extract<
    keyof NuggetProfileProps,
    'jobTitle' | 'employer' | 'school' | 'grade' | 'similarities'
  >;
  icon: ReactNode;
  maxLines: number;
}[] = [
  {
    key: 'jobTitle',
    icon: <AppIcon iconName="Briefcase01" diameter={ICON_SIZES['14px']} color="Gray/600" />,
    maxLines: 1,
  },
  {
    key: 'employer',
    icon: <AppIcon iconName="Building06" diameter={ICON_SIZES['14px']} color="Gray/600" />,
    maxLines: 1,
  },
  {
    key: 'school',
    icon: <AppIcon iconName="Bank" diameter={ICON_SIZES['14px']} color="Gray/600" />,
    maxLines: 1,
  },
  {
    key: 'grade',
    icon: <AppIcon iconName="Bank" diameter={ICON_SIZES['14px']} color="Gray/600" />,
    maxLines: 1,
  },
  {
    key: 'similarities',
    icon: (
      <Box sx={{ marginTop: ({ backrs }) => backrs.spacing['spacing-sm'] }}>
        <BadgeCircle size="sm" />
      </Box>
    ),
    maxLines: 3,
  },
];

export type NuggetProfileProps = Readonly<{
  abbreviatedName: string;
  location?: string;
  jobTitle?: string;
  employer?: string;
  school?: string;
  grade?: string;
  similarities?: string;
  isLoadingSimilarities?: boolean;
  profileImageUrl?: string;
  contentSize?: 'SMALL' | 'LARGE';
  profileType: 'EXTERNAL' | 'INTERNAL';
  role?: 'professional' | 'student' | 'foundations';
  onClick?: () => void;
  subText?: string;
  fitContent?: boolean;
  isLoadingSubText?: boolean;
  isHovered?: boolean;
  isHighlighted?: boolean;
  actionParams?: PanelNuggetActionParams;
}>;

export const NuggetProfile = ({
  abbreviatedName,
  jobTitle,
  employer,
  school,
  grade,
  similarities,
  isLoadingSimilarities = false,
  profileImageUrl,
  role,
  profileType,
  onClick,
  subText,
  fitContent = false,
  actionParams,
  isHovered,
  isHighlighted,
  isLoadingSubText = false,
}: NuggetProfileProps): ReactElement => {
  return (
    <BaseNugget
      subText={subText}
      onClick={onClick}
      fitContent={fitContent}
      isLoadingSubText={isLoadingSubText}
      isHighlighted={isHighlighted}
    >
      <Box sx={contentSx}>
        <Box sx={headerContainerSx}>
          <HoverButton
            hasBookmarked={actionParams?.hasBookmarked}
            handleActionBookmark={actionParams?.handleActionBookmark}
            handleActionAddTask={actionParams?.handleActionAddTask}
            handleActionShare={actionParams?.handleActionShare}
            isVisible={(!!actionParams && isHovered) || false}
            isAbsolutelyPositioned={true}
            position="flush"
          />
          <Box sx={profileImageAndNameContainerSx}>
            <Box sx={profileImageWrapperStyle}>
              {profileImageUrl ? (
                <Box component="img" src={profileImageUrl} sx={profileImgSx} />
              ) : (
                <AppIcon iconName="User02" diameter="96px" color="currentColor" />
              )}
              {role && <BackrsUserBadge variant={role} />}
            </Box>
            <Typography variant="text-lg-semibold" sx={abbreviatedNameStyle}>
              {abbreviatedName}
            </Typography>
          </Box>
        </Box>

        <Box sx={smallProfileInfoChipContainerStyle}>
          <Box sx={smallProfileInfoContainerStyle}>
            {INFO_CONFIG.map(({ key, icon, maxLines }) => {
              const props = { jobTitle, employer, school, grade, similarities };
              const displayValue = props[key];
              const isSimilaritiesLoading = key === 'similarities' && isLoadingSimilarities;

              return displayValue || isSimilaritiesLoading ? (
                <Box key={key} sx={infoRowStyle}>
                  <Box sx={iconContainerSx}>{icon}</Box>
                  {isSimilaritiesLoading ? (
                    <Skeleton variant="text" width="80%" height={20} />
                  ) : (
                    <Typography
                      variant="text-sm-regular"
                      color={({ backrs }) => backrs.colors['Gray/600']}
                      sx={profileAttributeTextSx(maxLines, key === 'similarities')}
                    >
                      {displayValue}
                    </Typography>
                  )}
                </Box>
              ) : null;
            })}
          </Box>

          <Box sx={chipContainerSx}>
            <TypeChip
              label={profileType === 'EXTERNAL' ? 'Real People' : 'Profile'}
              icon="User02"
            />
          </Box>
        </Box>
      </Box>
    </BaseNugget>
  );
};

const profileImgSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  aspectRatio: '1 / 1',
  borderRadius: backrs.radius['radius-full'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  width: '80px',
  height: '80px',
});

const abbreviatedNameStyle: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Gray/900'],
});

const contentSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
};

const headerContainerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: `calc(120px + ${backrs.spacing['spacing-8xl']})`,
});

const profileImageAndNameContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-2xl'],
});

const profileImageWrapperStyle: SxProps<Theme> = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
};

const smallProfileInfoContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  paddingTop: backrs.spacing['spacing-md'],
  flex: 1,
  gap: backrs.spacing['spacing-xxs'],
  height: '100%',
});

const smallProfileInfoChipContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  paddingBottom: backrs.spacing['spacing-none'],
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'space-between',
});

const chipContainerSx: SxProps<Theme> = ({ backrs }) => ({
  alignSelf: 'flex-start',
  justifySelf: 'flex-end',
  ml: backrs.spacing['spacing-none'],
});

const infoRowStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-sm'],
});

const iconContainerSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: backrs.spacing['spacing-none'],
});

const profileAttributeTextSx = (maxLines: number, isItalic = false): SxProps<Theme> => ({
  alignSelf: 'flex-start',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: maxLines,
  WebkitBoxOrient: 'vertical',
  fontStyle: isItalic ? 'italic' : 'normal',
});
